import { getClient, resolveEnvValue } from "./clients";

const normalizeBaseUrl = (url) => {
  if (!url) return "";
  return url.replace(/\/+$/, "");
};

export const withTrailingSlash = (url) => {
  if (!url) return url;
  return url.endsWith("/") ? url : `${url}/`;
};

// In development: /cases-api is proxied by setupProxy.js (avoids CORS).
// In production: hardcoded — no env var override to prevent CI/CD misconfiguration
// from silently rerouting traffic to the wrong backend.
export const API_BASE_URL = normalizeBaseUrl(
  process.env.NODE_ENV === "development"
    ? "/cases-api"
    : "https://bagic.newportalapi.camcom.ai"
);

export const PORTAL_BASE_URL = normalizeBaseUrl(
  process.env.REACT_APP_PORTAL_BASE_URL ||
    "https://bagicpp.portal.camcom.ai",
);

// Per-client API host overrides.
//
// Clients absent from this map keep using API_BASE_URL above, so the existing
// Bajaj/Ergo routing is untouched. Hegic is listed because it is a genuinely
// separate deployment, not another tenant behind the shared gateway:
// hegicpp.api.camcom.ai resolves to a different host (and a different IP) from
// bagic.newportalapi.camcom.ai, and answers POST /login on its own.
//
// This is deliberately driven by this in-repo map rather than by the value the
// portal-config API returns — that remote override was removed (see the note in
// apiService.js) because it served a stale host and silently rerouted traffic.
// Each client's own API host now lives in src/config/clients/<key>.json, next
// to everything else about that client, so adding one does not mean editing
// this file. A client with no `apiBaseUrl` keeps API_BASE_URL above — which is
// Bajaj's gateway, and Bajaj is the client that should be on it.
//
// This deliberately reads the in-repo files rather than the value the
// portal-config API returns: that remote override was removed (see the note in
// apiService.js) because it served a stale host and silently rerouted traffic.
const clientApiBaseUrl = (key) =>
  resolveEnvValue(getClient(key)?.apiBaseUrl);

/**
 * The API host for a given client, defaulting to whoever is logged in.
 *
 * Resolved at call time, not module load, because the client is only known
 * after login. Pass `sourceName` explicitly from the login flow itself — at
 * that point the session has not been written to localStorage yet, so the
 * stored value still refers to the previous client (or nothing at all).
 *
 * Falls back to API_BASE_URL for any client without an override.
 */
/**
 * Which backend a client resolved to, said once per distinct answer.
 *
 * Called on every request through apiClient's interceptor, so logging each call
 * would bury everything else; the answer only changes when the logged-in client
 * does. Silent in production.
 */
const tracedResolutions = new Set();

const traceApiHost = (key, resolved, overridden) => {
  if (process.env.NODE_ENV === "production") return;
  const line = `${key || "(no client in session)"} → ${resolved}${
    overridden ? "" : "  [shared gateway, no per-client override]"
  }`;
  if (tracedResolutions.has(line)) return;
  tracedResolutions.add(line);
  console.info("[urls] API host:", line);
};

export const getApiBaseUrl = (sourceName) => {
  let key = sourceName || null;
  try {
    key = key || localStorage.getItem("source_name");
    const override = clientApiBaseUrl(key);
    if (override) {
      const resolved = normalizeBaseUrl(override);
      traceApiHost(key, resolved, true);
      return resolved;
    }
  } catch {
    /* localStorage unavailable — fall through to the shared gateway */
  }
  traceApiHost(key, API_BASE_URL, false);
  return API_BASE_URL;
};

// Dedicated portal configuration service — handles login, portal-config CRUD.
//
// In development the CRA dev server (setupProxy.js) proxies /api/* requests
// to portal-configuration-api.dev.camcom.ai, so we use an empty base URL and
// let requests go to the same origin — no CORS preflight.
//
// In production set REACT_APP_PORTAL_CONFIG_API_URL to the full URL (or point
// to a same-origin backend proxy).
export const PORTAL_CONFIG_API_URL = normalizeBaseUrl(
  process.env.REACT_APP_PORTAL_CONFIG_API_URL ||
    (process.env.NODE_ENV === "development"
      ? ""
      : "https://portal-configuration-api.dev.camcom.ai"),
);

/**
 * The host that answers GET /get_analytics_data for a client.
 *
 * Ergo's motor line needs this because analytics is NOT on the host the rest of
 * motor uses. Probed unauthenticated — 401 means the route exists, 404 means it
 * does not:
 *
 *   ergobaltic.dev.aiapi.camcom.ai  /get_analytics_data 404   /assessment_new 405
 *   ergobaltic.dev.api.camcom.ai    /get_analytics_data 401   /assessment_new 404
 *
 * All three Ergo names resolve to 216.48.183.86: they are Host-routed apps on
 * one box whose route sets overlap but are not equal. So moving the client to
 * the analytics host would trade a working analytics page for a broken case
 * detail one; only this route is redirected.
 *
 * A client with no `analyticsBaseUrl` — the property line, whose own host
 * serves analytics — falls back to its API host, so nothing else changes.
 */
export const getAnalyticsBaseUrl = (sourceName) => {
  try {
    const key = sourceName || localStorage.getItem("source_name");
    const override = resolveEnvValue(getClient(key)?.analyticsBaseUrl);
    if (override) return normalizeBaseUrl(override);
  } catch {
    /* localStorage unavailable — fall through to the client's API host */
  }
  return getApiBaseUrl(sourceName);
};

/**
 * The client's LEGACY PORTAL origin — distinct from its API host.
 *
 * Hegic's digital-passport endpoint lives only there and authenticates with that
 * portal's session cookie, not the Bearer token everything else uses. In
 * development it goes through /hegic-portal (see setupProxy.js) so the browser
 * treats it as same-origin; in production an empty base means same-origin too,
 * which is correct when this SPA is served from the portal domain itself.
 *
 * Returns null for clients with no such endpoint, so callers can skip rather
 * than fire a request that cannot succeed.
 */
export const getPortalOrigin = (sourceName) => {
  try {
    const key = sourceName || localStorage.getItem("source_name");
    const client = getClient(key);
    if (client && "portalOrigin" in client) {
      return normalizeBaseUrl(resolveEnvValue(client.portalOrigin));
    }
  } catch {
    /* localStorage unavailable */
  }
  return null;
};
